import { Container } from '@/components/Container';
import { getPublished } from '@/data/loader';
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import googleThumbs from '../../../public/images/google/thumbs.json';

/* ═══════════════════════════════════════════
   ONDE COMER — Restaurantes em destaque
   ═══════════════════════════════════════════ */

export function OndeComerSection() {
  const { t } = useTranslation();
  const restaurantes = getPublished('restaurante');
  const destaques = restaurantes.slice(0, 6);

  return (
    <section id="onde-comer" className="py-16 sm:py-20 bg-sand-50">
      <Container>
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.dining.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.dining.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.dining.subtitle')}
          </p>
        </div>

        {/* Introdução editorial */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-teal-50 border-l-4 border-teal-deep rounded-r-xl p-5">
            <p className="font-editorial text-base text-warm-gray italic">
              A culinária da Ilha do Mel é ditada pelo mar. Tainha defumada (especialmente em junho
              e julho, época do Festival da Tainha), camarão fresco na chapa, caldeirada de frutos
              do mar e peixe grelhado são os pratos que definem a ilha. Os melhores frutos do mar
              chegam direto dos pescadores locais — procure os restaurantes familiares menores para
              a experiência mais autêntica.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {destaques.map((r: any) => (
            <Link
              key={r.id}
              to={`/restaurante/${r.handle}`}
              className="group bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={(googleThumbs as Record<string, string>)[r.handle] || r.image}
                  alt={r.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-body text-xs text-teal-deep font-medium uppercase">
                    {r.vilarejoName}
                  </span>
                  {r.provider?.commission && (
                    <span className="bg-coral text-white text-xs font-body font-bold px-2 py-0.5 rounded-full">
                      {t('guide.dining.partnerBadge')}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-xl text-warm-dark mb-1">
                  {r.title.toUpperCase()}
                </h3>
                <p className="font-body text-sm text-warm-gray mb-3 line-clamp-2">{r.subtitle}</p>
                <div className="flex items-center justify-between text-xs font-body text-warm-gray">
                  <span>
                    {Array.isArray(r.cuisine) && r.cuisine.length > 0
                      ? r.cuisine.join(', ')
                      : r.cuisine}
                  </span>
                  <span className="font-semibold text-warm-dark">{r.priceRange?.indicator}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Alerta sazonal */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 mt-6">
          <span role="img" aria-label="Atenção" className="text-xl flex-shrink-0">
            🗓️
          </span>
          <div>
            <p className="font-body text-sm font-semibold text-warm-dark">
              Funcionamento na baixa temporada
            </p>
            <p className="font-body text-sm text-warm-gray">
              Em abril–junho e agosto–setembro, muitos restaurantes fecham de segunda a quinta.
              Confirme sempre o funcionamento antes de ir — especialmente se planeja jantar fora do
              fim de semana. Os mercadinhos locais são ótimas opções para café da manhã e lanches.
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/restaurantes-ilha-do-mel"
            className="inline-flex items-center gap-2 font-body text-sm font-semibold text-teal-deep hover:text-teal-700 transition-colors"
          >
            {t('guide.dining.seeAll')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </Container>
    </section>
  );
}
